import { useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
// @ts-expect-error dependency missing types
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import ApprovalButtons from '../consumer/ApprovalButtons';
import ApprovalCancelNote from './ApprovalCancelNote';
import ApprovalDataErrorMessage from './ApprovalDataErrorMessage';
import ApprovalEmptyState from './ApprovalEmptyState';
import ApprovalNotRequested from './ApprovalNotRequested';
import ApprovalRequestNote from './ApprovalRequestNote';
import ApprovalStepsList from './ApprovalStepsList';
import ApprovalSummary from './ApprovalSummary';
import withErrorBoundary from '../../hoc/withErrorBoundary';
import * as trackingConstants from '../../constants/trackingConstants';
import { trackInteraction } from '../../utils/usageTracker.ts';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import useApprovalConditions from '../../hooks/useApprovalConditions';
import ApprovalReassignButton from '../editPanel/ApprovalReassignButton';
import ApprovalNotEnabled from './ApprovalNotEnabled';
import { ApprovalTypes } from '../../types';
import ReleaseRibbon from '../common/ReleaseRibbon';
import ApprovalUnifiedEvents from '../unified-events/ApprovalUnifiedEvents';
import getKnowledgeBaseUrl from '../../utils/getKnowledgeBaseUrl';
import ApprovalSidebarStatusHeading from './ApprovalSidebarStatusHeading';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function ApprovalSidebarWrapper(props) {
  const {
    children,
    approvalType
  } = props;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    "data-test-id": "product-approvals-approval-sidebar",
    blockSize: "100%",
    position: "relative",
    children: [/*#__PURE__*/_jsx(ReleaseRibbon, {
      approvalType: approvalType
    }), children, approvalType !== ApprovalTypes.QUOTE && /*#__PURE__*/_jsx(Flex, {
      alignItems: "end",
      flexGrow: 1,
      paddingInlineStart: "500",
      paddingBlockEnd: "600",
      children: /*#__PURE__*/_jsx(View, {
        as: "span",
        marginInlineStart: "100",
        children: /*#__PURE__*/_jsx(KnowledgeBaseButton, {
          url: getKnowledgeBaseUrl(approvalType),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "productApprovals.approvalsKnowledgeBaseLink"
          })
        })
      })
    })]
  });
}
function ApprovalSidebar(props) {
  const {
    historyMode,
    alertContent,
    approvalButtonProps = {}
  } = props;
  const approval = useApprovalContext();
  const approvalType = approval.meta.approvalType;
  const {
    isApproved,
    isCancelled,
    isConcludedAndShouldRestartOnConclude,
    isEnabled,
    isLoading,
    isPending,
    isRejected,
    noApprovalData
  } = useApprovalConditions();
  useEffect(() => {
    const extra = {
      enabled: approval.config.enabled
    };
    trackInteraction(trackingConstants.APPROVAL_SIDEBAR_OPENED, approvalType, extra);
  }, [approvalType, approval.config.enabled]);
  const onOpenActivitySection = evt => {
    if (evt.target.value) {
      trackInteraction(trackingConstants.APPROVAL_SIDEBAR_ACTIVITY_SECTION_OPENED, approvalType);
    }
  };
  if (approval.previewSidebarContent) {
    return /*#__PURE__*/_jsxs(ApprovalSidebarWrapper, {
      approvalType: approvalType,
      children: [approval.previewSidebarContent, !noApprovalData && /*#__PURE__*/_jsx(ExpandableSection, {
        onOpenChange: onOpenActivitySection,
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalSidebar.activity"
        }),
        indentation: "compact",
        "data-test-id": "product-approvals-activity-feed-expandable",
        children: /*#__PURE__*/_jsx(ApprovalUnifiedEvents, {})
      })]
    });
  }
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 150,
      layout: "centered"
    });
  }
  if (approval.meta.httpErrors.fetchData) {
    return /*#__PURE__*/_jsx(ApprovalDataErrorMessage, {});
  }
  if (!isEnabled) {
    return /*#__PURE__*/_jsx(ApprovalSidebarWrapper, {
      approvalType: approvalType,
      children: /*#__PURE__*/_jsx(ApprovalNotEnabled, {})
    });
  }
  if (historyMode && (noApprovalData || !isApproved)) {
    return /*#__PURE__*/_jsx(ApprovalSidebarWrapper, {
      approvalType: approvalType,
      children: /*#__PURE__*/_jsx(ApprovalNotRequested, {
        approvalType: approvalType,
        approvalAction: approval.meta.approvalAction
      })
    });
  }
  if (noApprovalData || isApproved && isConcludedAndShouldRestartOnConclude) {
    return /*#__PURE__*/_jsxs(ApprovalSidebarWrapper, {
      approvalType: approvalType,
      children: [alertContent, /*#__PURE__*/_jsx(ApprovalEmptyState, {})]
    });
  }
  return /*#__PURE__*/_jsxs(ApprovalSidebarWrapper, {
    approvalType: approvalType,
    children: [/*#__PURE__*/_jsxs(View, {
      padding: "500",
      marginInlineEnd: "500",
      as: "div",
      children: [/*#__PURE__*/_jsx(ApprovalSidebarStatusHeading, {}), alertContent && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "300",
        as: "div",
        children: alertContent
      }), /*#__PURE__*/_jsx(ApprovalSummary, {}), approvalType !== ApprovalTypes.DEAL && /*#__PURE__*/_jsx(ApprovalButtons, Object.assign({}, approvalButtonProps, {
        buttonProps: {
          size: 'sm',
          marginBlockStart: '500'
        },
        primaryButtonUse: "tertiary",
        secondaryButtonUse: "tertiary-light",
        hidePendingButton: true,
        children: /*#__PURE__*/_jsx(_Fragment, {})
      }))]
    }), (isPending || isApproved || isRejected) && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ExpandableSection, {
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalSidebar.requestNote"
        }),
        indentation: "compact",
        defaultOpen: true,
        children: /*#__PURE__*/_jsx(ApprovalRequestNote, {})
      }), /*#__PURE__*/_jsx(ExpandableSection, {
        actions: /*#__PURE__*/_jsx(ApprovalReassignButton, {}),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.approvers"
        }),
        indentation: "compact",
        defaultOpen: true,
        children: /*#__PURE__*/_jsx(ApprovalStepsList, {})
      })]
    }), isCancelled && /*#__PURE__*/_jsx(ExpandableSection, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.ApprovalSidebar.recallNote"
      }),
      indentation: "compact",
      defaultOpen: true,
      children: /*#__PURE__*/_jsx(ApprovalCancelNote, {})
    }), !noApprovalData && /*#__PURE__*/_jsx(ExpandableSection, {
      onOpenChange: onOpenActivitySection,
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.ApprovalSidebar.activity"
      }),
      indentation: "compact",
      "data-test-id": "product-approvals-activity-feed-expandable",
      children: /*#__PURE__*/_jsx(ApprovalUnifiedEvents, {})
    })]
  });
}
export default withErrorBoundary(ApprovalSidebar);